/**
 * Client-side validation for the schedule dialog, mirroring the calendar service's boundary
 * rules (calendar/internal/api/events.go): title 1..200 runes, RFC 3339 timestamps with
 * endsAt strictly after startsAt, roomSlug ^[a-z0-9][a-z0-9-]{2,63}$. Validation here keeps
 * obviously bad input off the wire; the server re-validates everything.
 */

/** The control-service room slug shape (docs/contract.md), enforced again client-side. */
export const ROOM_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{2,63}$/;

/** Title bounds, copied from the service's maxTitleRunes. */
export const MAX_TITLE_RUNES = 200;

/** Roster bounds, copied from the service's maxParticipants. */
export const MAX_PARTICIPANTS = 50;

/** Description bound, copied from the service's maxDescriptionRunes. */
export const MAX_DESCRIPTION_RUNES = 4000;

/** Capacity bound, copied from the service's normalizeCapacity (CR-27); 0 = unlimited. */
export const MAX_CAPACITY = 100000;

/** Description: at most 4000 runes when present (empty is fine - it clears the field). */
export function validateDescription(raw: string): FieldError {
  if ([...raw].length > MAX_DESCRIPTION_RUNES) {
    return 'calendar.schedule.errors.description_too_long';
  }
  return null;
}

/**
 * Parses the capacity input (CR-27): '' reads as 0 (unlimited - the server stores NULL), a
 * non-negative integer up to MAX_CAPACITY reads as itself (0 = unlimited too). null = invalid
 * input; the caller flags its own error key.
 */
export function parseCapacityInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return 0;
  }
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const value = Number(trimmed);
  if (value > MAX_CAPACITY) {
    return null;
  }
  return value;
}

/** Result of validating one field: a message key when invalid, null when acceptable. */
export type FieldError = string | null;

/** Title: non-blank, at most 200 runes (the service trims before counting). */
export function validateTitle(raw: string): FieldError {
  const title = raw.trim();
  if (title.length === 0) {
    return 'calendar.schedule.errors.title_required';
  }
  if ([...title].length > MAX_TITLE_RUNES) {
    return 'calendar.schedule.errors.title_too_long';
  }
  return null;
}

/** yyyy-mm-dd (the native date input's value). */
export function isValidDateString(raw: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (match === null) {
    return false;
  }
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, monthIndex, day);
  return date.getFullYear() === year && date.getMonth() === monthIndex && date.getDate() === day;
}

/** HH:MM (the native time input's value). */
export function isValidTimeString(raw: string): boolean {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(raw);
  return match !== null;
}

/** Duration in minutes: a positive integer, capped at 24 hours. */
export function validateDurationMinutes(raw: number): FieldError {
  if (!Number.isInteger(raw) || raw <= 0) {
    return 'calendar.schedule.errors.duration_invalid';
  }
  if (raw > 24 * 60) {
    return 'calendar.schedule.errors.duration_too_long';
  }
  return null;
}

/** Optional room slug: empty clears it, otherwise the control-service slug shape. */
export function validateRoomSlug(raw: string): FieldError {
  const slug = raw.trim();
  if (slug.length === 0) {
    return null;
  }
  return ROOM_SLUG_PATTERN.test(slug) ? null : 'calendar.schedule.errors.room_slug_invalid';
}

/** One participant chip: non-blank, at most 200 runes, roster not yet full, no duplicates inside the draft. */
export function validateParticipant(raw: string, existing: readonly string[]): FieldError {
  const name = raw.trim();
  if (name.length === 0) {
    return 'calendar.schedule.errors.participant_blank';
  }
  if ([...name].length > MAX_PARTICIPANTS_NAME_RUNES) {
    return 'calendar.schedule.errors.participant_too_long';
  }
  if (existing.length >= MAX_PARTICIPANTS) {
    return 'calendar.schedule.errors.participants_limit';
  }
  if (existing.some((candidate) => candidate.toLowerCase() === name.toLowerCase())) {
    return 'calendar.schedule.errors.participant_duplicate';
  }
  return null;
}

/** Per-entry rune bound of the roster (the service's maxParticipantRunes). */
export const MAX_PARTICIPANTS_NAME_RUNES = 200;

/** Outcome of parsing the event dialog's multi-line participants input. */
export interface ParticipantsParse {
  /** Trimmed, de-duplicated (case-insensitively) roster in input order; blank lines dropped. */
  readonly participants: readonly string[];
  /** A message key when a line violates the roster rules, null when the input is acceptable. */
  readonly error: FieldError;
}

/**
 * Parses the event dialog's participants textarea (one entry per line, CR-21 roster rules):
 * blank lines are dropped, entries are trimmed, duplicates collapse case-insensitively onto
 * the first occurrence, and the bounds mirror the service (50 entries, 200 runes each). Pure
 * so the dialog validates on submit and tests can pin the rules.
 */
export function parseParticipantsInput(raw: string): ParticipantsParse {
  const participants: string[] = [];
  for (const line of raw.split('\n')) {
    const name = line.trim();
    if (name.length === 0) {
      continue;
    }
    if ([...name].length > MAX_PARTICIPANTS_NAME_RUNES) {
      return { participants, error: 'calendar.schedule.errors.participant_too_long' };
    }
    if (participants.length >= MAX_PARTICIPANTS) {
      return { participants, error: 'calendar.schedule.errors.participants_limit' };
    }
    if (participants.some((candidate) => candidate.toLowerCase() === name.toLowerCase())) {
      continue;
    }
    participants.push(name);
  }
  return { participants, error: null };
}

/** Outcome of validating the whole schedule draft. */
export interface ScheduleDraft {
  readonly title: string;
  readonly date: string;
  readonly time: string;
  readonly durationMinutes: number;
  readonly location: string;
  readonly roomSlug: string;
  readonly participants: readonly string[];
  readonly description: string;
  /** The registration cap as typed (CR-27): '' = unlimited. */
  readonly capacity: string;
}

/**
 * Assembles the API body fields from a validated draft. startsAt/endsAt are local-time RFC 3339
 * stamps (the browser sends its zone offset, the service stores absolute time). Participant
 * chips persist into the event's participants field (migration 1004). The description and the
 * capacity (CR-27) ride when set: '' / 0 = unlimited = absent on create.
 */
export function draftToCreateBody(draft: ScheduleDraft): {
  readonly title: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly description?: string;
  readonly location?: string;
  readonly roomSlug?: string;
  readonly participants?: readonly string[];
  readonly capacity?: number;
} {
  const start = draftStart(draft);
  const startsAt = toRfc3339(start);
  const endsAt = toRfc3339(new Date(start.getTime() + draft.durationMinutes * 60 * 1000));
  const description = draft.description.trim().length > 0 ? draft.description.trim() : undefined;
  const capacity = parseCapacityInput(draft.capacity);
  return {
    title: draft.title.trim(),
    startsAt,
    endsAt,
    ...(description !== undefined ? { description } : {}),
    ...(draft.location.trim().length > 0 ? { location: draft.location.trim() } : {}),
    ...(draft.roomSlug.trim().length > 0 ? { roomSlug: draft.roomSlug.trim() } : {}),
    ...(draft.participants.length > 0 ? { participants: draft.participants } : {}),
    ...(capacity !== null && capacity > 0 ? { capacity } : {}),
  };
}

/** Local Date of the draft's date+time; invalid parts fall back to the epoch (callers validate first). */
function draftStart(draft: ScheduleDraft): Date {
  return combineLocal(draft.date, draft.time);
}

/**
 * Local Date from a yyyy-mm-dd date and an HH:MM time (the native inputs' values). An empty
 * time means midnight - the all-day form.
 */
export function combineLocal(date: string, time: string): Date {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  const [hour = 0, minute = 0] = time.split(':').map(Number);
  return new Date(year, month - 1, day, hour, minute);
}

/** Local-time RFC 3339 with the browser's offset (the same form `new Date().toISOString()` keeps). */
export function toRfc3339(date: Date): string {
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absOffset = Math.abs(offsetMinutes);
  const offsetHours = `${Math.floor(absOffset / 60)}`.padStart(2, '0');
  const offsetMins = `${absOffset % 60}`.padStart(2, '0');
  const pad = (value: number): string => `${value}`.padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
    `${sign}${offsetHours}:${offsetMins}`
  );
}
